import React, { useState, memo } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// ISO 3166-1 numeric → region mapping
const COUNTRY_REGION = {
  // North America
  '840': 'North America', '124': 'North America', '484': 'North America',
  '044': 'North America', '052': 'North America', '084': 'North America',
  '188': 'North America', '192': 'North America', '214': 'North America',
  '222': 'North America', '320': 'North America', '332': 'North America',
  '340': 'North America', '388': 'North America', '558': 'North America',
  '591': 'North America', '630': 'North America', '780': 'North America',
  '670': 'North America', '659': 'North America', '662': 'North America',
  // Latin America
  '032': 'Latin America', '068': 'Latin America', '076': 'Latin America',
  '152': 'Latin America', '170': 'Latin America', '218': 'Latin America',
  '328': 'Latin America', '600': 'Latin America', '604': 'Latin America',
  '740': 'Latin America', '858': 'Latin America', '862': 'Latin America',
  // Europe
  '826': 'Europe', '276': 'Europe', '250': 'Europe', '724': 'Europe',
  '380': 'Europe', '528': 'Europe', '056': 'Europe', '040': 'Europe',
  '756': 'Europe', '620': 'Europe', '372': 'Europe', '208': 'Europe',
  '752': 'Europe', '578': 'Europe', '246': 'Europe', '616': 'Europe',
  '203': 'Europe', '703': 'Europe', '348': 'Europe', '642': 'Europe',
  '100': 'Europe', '191': 'Europe', '705': 'Europe', '300': 'Europe',
  '008': 'Europe', '688': 'Europe', '499': 'Europe', '807': 'Europe',
  '070': 'Europe', '352': 'Europe', '440': 'Europe', '428': 'Europe',
  '233': 'Europe', '442': 'Europe',
  // CIS
  '643': 'CIS', '804': 'CIS', '112': 'CIS', '398': 'CIS',
  '860': 'CIS', '795': 'CIS', '762': 'CIS', '417': 'CIS',
  '031': 'CIS', '051': 'CIS', '268': 'CIS', '498': 'CIS',
  // Middle East
  '792': 'Middle East', '364': 'Middle East', '368': 'Middle East',
  '682': 'Middle East', '784': 'Middle East', '376': 'Middle East',
  '400': 'Middle East', '422': 'Middle East', '512': 'Middle East',
  '634': 'Middle East', '048': 'Middle East', '414': 'Middle East',
  '887': 'Middle East', '760': 'Middle East', '275': 'Middle East',
  // Africa
  '012': 'Africa', '024': 'Africa', '072': 'Africa', '854': 'Africa',
  '108': 'Africa', '120': 'Africa', '132': 'Africa', '140': 'Africa',
  '148': 'Africa', '174': 'Africa', '178': 'Africa', '180': 'Africa',
  '262': 'Africa', '818': 'Africa', '226': 'Africa', '232': 'Africa',
  '748': 'Africa', '231': 'Africa', '266': 'Africa', '270': 'Africa',
  '288': 'Africa', '324': 'Africa', '624': 'Africa', '384': 'Africa',
  '404': 'Africa', '426': 'Africa', '430': 'Africa', '434': 'Africa',
  '450': 'Africa', '454': 'Africa', '466': 'Africa', '478': 'Africa',
  '480': 'Africa', '504': 'Africa', '508': 'Africa', '516': 'Africa',
  '562': 'Africa', '566': 'Africa', '646': 'Africa', '678': 'Africa',
  '686': 'Africa', '694': 'Africa', '706': 'Africa', '710': 'Africa',
  '728': 'Africa', '729': 'Africa', '800': 'Africa', '834': 'Africa',
  '768': 'Africa', '788': 'Africa', '894': 'Africa', '716': 'Africa',
  // Asia
  '156': 'Asia', '392': 'Asia', '410': 'Asia', '356': 'Asia',
  '360': 'Asia', '458': 'Asia', '764': 'Asia', '704': 'Asia',
  '608': 'Asia', '104': 'Asia', '050': 'Asia', '144': 'Asia',
  '524': 'Asia', '586': 'Asia', '004': 'Asia', '496': 'Asia',
  '408': 'Asia', '418': 'Asia', '116': 'Asia', '626': 'Asia',
  '158': 'Asia', '702': 'Asia', '096': 'Asia', '064': 'Asia',
  // Oceania
  '036': 'Oceania', '554': 'Oceania', '598': 'Oceania', '090': 'Oceania',
  '242': 'Oceania', '540': 'Oceania', '548': 'Oceania', '882': 'Oceania',
  '776': 'Oceania',
};

const REGION_COLORS = {
  'North America': { base: '#ec4899', light: '#fce7f3', glow: 'rgba(236,72,153,0.4)' },
  'Europe':        { base: '#8b5cf6', light: '#ede9fe', glow: 'rgba(139,92,246,0.4)' },
  'Asia':          { base: '#06b6d4', light: '#cffafe', glow: 'rgba(6,182,212,0.4)' },
  'CIS':           { base: '#f59e0b', light: '#fef3c7', glow: 'rgba(245,158,11,0.4)' },
  'Latin America': { base: '#10b981', light: '#d1fae5', glow: 'rgba(16,185,129,0.4)' },
  'Africa':        { base: '#f97316', light: '#ffedd5', glow: 'rgba(249,115,22,0.4)' },
  'Middle East':   { base: '#eab308', light: '#fef9c3', glow: 'rgba(234,179,8,0.4)' },
  'Oceania':       { base: '#3b82f6', light: '#dbeafe', glow: 'rgba(59,130,246,0.4)' },
};

const RegionHeatMap = ({ regionCounts = {} }) => {
  const [hovered, setHovered] = useState(null);
  const totalArticles = Object.values(regionCounts).reduce((s, c) => s + c, 0);
  if (totalArticles === 0) return null;

  const maxCount = Math.max(...Object.values(regionCounts), 1);
  const activeRegions = Object.entries(regionCounts).filter(([, c]) => c > 0).sort((a, b) => b[1] - a[1]);

  const getCountryStyle = (geo) => {
    const id = geo.id || geo.properties?.iso_n3;
    const region = COUNTRY_REGION[id];
    const count = region ? (regionCounts[region] || 0) : 0;
    const colors = REGION_COLORS[region];
    const isHovered = hovered === region;

    if (!count || !colors) {
      return {
        default: { fill: '#1e293b', stroke: '#334155', strokeWidth: 0.4, outline: 'none', transition: 'all 0.3s ease' },
        hover: { fill: '#334155', stroke: '#475569', strokeWidth: 0.4, outline: 'none', transition: 'all 0.3s ease' },
        pressed: { fill: '#334155', stroke: '#475569', strokeWidth: 0.4, outline: 'none' },
      };
    }

    const intensity = Math.min(count / maxCount, 1);
    const opacity = 0.5 + intensity * 0.5;

    return {
      default: {
        fill: colors.base,
        fillOpacity: isHovered ? Math.min(opacity + 0.15, 1) : opacity,
        stroke: isHovered ? colors.light : '#1e293b',
        strokeWidth: isHovered ? 1 : 0.4,
        outline: 'none',
        transition: 'all 0.3s ease',
        filter: isHovered ? `drop-shadow(0 0 12px ${colors.glow})` : `drop-shadow(0 0 4px ${colors.glow})`,
      },
      hover: {
        fill: colors.base,
        fillOpacity: Math.min(opacity + 0.15, 1),
        stroke: colors.light,
        strokeWidth: 1,
        outline: 'none',
        transition: 'all 0.3s ease',
        filter: `drop-shadow(0 0 12px ${colors.glow})`,
      },
      pressed: {
        fill: colors.base,
        fillOpacity: opacity,
        stroke: colors.light,
        strokeWidth: 1,
        outline: 'none',
      },
    };
  };

  return (
    <div className="rounded-xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 40%, #0f172a 100%)' }}>
      {/* Map */}
      <div className="relative z-10" style={{ margin: '-8px -4px -16px' }}>
        <ComposableMap
          projection="geoNaturalEarth1"
          projectionConfig={{ scale: 140, center: [10, 10] }}
          width={800}
          height={360}
          style={{ width: '100%', height: 'auto' }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const id = geo.id || geo.properties?.iso_n3;
                const region = COUNTRY_REGION[id];
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    onMouseEnter={() => region && regionCounts[region] && setHovered(region)}
                    onMouseLeave={() => setHovered(null)}
                    style={getCountryStyle(geo)}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>
      </div>

      {/* Region legend */}
      <div className="relative z-10 px-3 pb-3 -mt-1">
        <div className="flex flex-wrap gap-x-3 gap-y-1">
          {activeRegions.map(([region, count]) => {
            const colors = REGION_COLORS[region];
            const pct = Math.round((count / totalArticles) * 100);
            const isHov = hovered === region;
            return (
              <div
                key={region}
                className="flex items-center gap-1 cursor-default transition-all duration-200"
                style={{ opacity: hovered && !isHov ? 0.4 : 1 }}
                onMouseEnter={() => setHovered(region)}
                onMouseLeave={() => setHovered(null)}
              >
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0 transition-all duration-200"
                  style={{
                    backgroundColor: colors?.base,
                    boxShadow: isHov ? `0 0 6px ${colors?.glow}` : 'none',
                  }}
                />
                <span className={`text-[10px] font-medium ${isHov ? 'text-white' : 'text-slate-200'} transition-colors duration-200`}>
                  {region}
                </span>
                <span className="text-[10px] text-slate-300 font-bold">{count}</span>
                <span className="text-[10px] text-slate-400">({pct}%)</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Hover tooltip */}
      {hovered && regionCounts[hovered] && (
        <div
          className="absolute top-2 right-2 z-20 rounded-lg px-3 py-2 shadow-xl border"
          style={{
            background: 'rgba(15,23,42,0.9)',
            backdropFilter: 'blur(12px)',
            borderColor: REGION_COLORS[hovered]?.glow || 'rgba(255,255,255,0.1)',
          }}
        >
          <div className="flex items-center gap-1.5 mb-0.5">
            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: REGION_COLORS[hovered]?.base }} />
            <span className="text-xs font-bold text-white">{hovered}</span>
          </div>
          <div className="text-xs text-slate-300">
            <span className="font-bold text-white">{regionCounts[hovered]}</span>
            <span className="ml-1">article{regionCounts[hovered] !== 1 ? 's' : ''}</span>
            <span className="text-slate-500 ml-1">&middot; {Math.round((regionCounts[hovered] / totalArticles) * 100)}%</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(RegionHeatMap);
